import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { InputInsert } from '../../types/mold';
import { FileResponse, ImageRecord } from '../../types/moldOrder';
import { fileService } from '../../services/api';
import ImagesUploader from '../Questionnaire/ImagesUploader';
import ActionButton from './ActionButton';
import { ImageIcon } from 'lucide-react';

interface ImageUploadModalProps {
    onClose: () => void;
    selectedInsert?: InputInsert | null;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({ onClose, selectedInsert }) => {
    const [files, setFiles] = useState<FileResponse[]>([]);
    const [imageMap, setImageMap] = useState<Record<string, ImageRecord>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isInUploadMode, setIsInUploadMode] = useState(false);

    // Fetch files for the selected insert
    useEffect(() => {
        const fetchInsertFiles = async () => {
            if (!selectedInsert?.id) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const response = await fileService.getFiles('insert', selectedInsert.id);
                const fileData = response.data || [];
                setFiles(fileData);

                // Initialize image map with loading states
                const initialImageMap = fileData.reduce((acc: Record<string, ImageRecord>, file) => {
                    acc[file.fileName] = { fileName: file.fileName, blob: '', loading: true, error: false };
                    return acc;
                }, {});
                setImageMap(initialImageMap);

                // Fetch blob data for each file
                const previews = await Promise.all(fileData.map(async (file) => {
                    try {
                        const blobResponse = await fileService.downloadFile(file.fileName);
                        const blobUrl = URL.createObjectURL(blobResponse.data);
                        return { fileName: file.fileName, blobUrl, error: false };
                    } catch (error) {
                        console.error('Error fetching blob for file:', file.fileName, error);
                        return { fileName: file.fileName, blobUrl: '', error: true };
                    }
                }));

                // Update image map with blob URLs
                const updatedImageMap = { ...initialImageMap };
                previews.forEach(({ fileName, blobUrl, error }) => {
                    updatedImageMap[fileName] = { fileName, blob: blobUrl, loading: false, error };
                });
                setImageMap(updatedImageMap);

            } catch (err) {
                console.error('Failed to fetch insert files:', err);
                setError('ไม่สามารถโหลดรูปภาพได้');
            } finally {
                setLoading(false);
            }
        };

        fetchInsertFiles();
    }, [selectedInsert?.id]);

    // Cleanup blob URLs on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            Object.values(imageMap).forEach(record => {
                if (record.blob && record.blob.startsWith('blob:')) {
                    URL.revokeObjectURL(record.blob);
                }
            });
        };
    }, [imageMap]);

    if (!selectedInsert) {
        return (
            <Modal onClose={onClose} hideTopPadding={false} isBackgroundTransparent={true}>
                <div className="text-center py-8">
                    <p className="text-gray-500">ไม่พบข้อมูลอินเสิร์ท</p>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            onClose={onClose}
            hideTopPadding={false}
            isBackgroundTransparent={true}
            maxWidth="max-w-4xl"
        >
            <div className="px-6">
                {/* Header */}
                <div className="flex flex-row justify-between items-center border-b py-2">
                    <h2 className="text-xl font-semibold text-gray-900">
                        รูปภาพอินเสิร์ท: {selectedInsert.name}
                    </h2>
                    <p className="text-sm text-gray-500">
                        รหัส: {selectedInsert.id} | สถานะ: {
                            selectedInsert.status === 'active' ? 'ใช้งาน' :
                                selectedInsert.status === 'in_used' ? 'กำลังใช้งาน' : 'ยกเลิก'
                        }
                    </p>
                </div>

                {/* Content */}
                <div className="min-h-[400px]">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <span className="ml-3 text-gray-600">กำลังโหลดรูปภาพ...</span>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="bg-red-50 p-4 rounded-full mb-4">
                                <ImageIcon size={32} className="text-red-500" />
                            </div>
                            <p className="text-red-600 font-medium mb-2">เกิดข้อผิดพลาด</p>
                            <p className="text-gray-500 text-sm">{error}</p>
                            <ActionButton
                                onClick={() => window.location.reload()}
                                color="outline-primary"
                                size="sm"
                                className="mt-4"
                            >
                                ลองใหม่อีกครั้ง
                            </ActionButton>
                        </div>
                    ) : (files.length === 0 && !isInUploadMode) ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="bg-gray-50 p-6 rounded-full mb-4">
                                <ImageIcon size={32} className="text-gray-400" />
                            </div>
                            <p className="text-gray-600 font-medium mb-2">ยังไม่มีรูปภาพ</p>
                            <p className="text-gray-500 text-sm mb-4">
                                คุณยังไม่ได้อัปโหลดรูปภาพสำหรับอินเสิร์ทนี้
                            </p>
                            <ActionButton
                                onClick={() => setIsInUploadMode(true)}
                                color="outline-primary"
                                size="md"
                                className="rounded-full shadow-sm"
                            >
                                อัปโหลดรูป
                            </ActionButton>
                        </div>
                    ) : (
                        <div className="space-y-4 pt-2 h-full">
                            <ImagesUploader
                                entityType="insert"
                                entityId={selectedInsert.id!}
                                title="insert_images"
                                displayTitle=""
                                files={files}
                                onFileChange={() => { }}
                                maxPhotoCount={3}
                                displayStyle="default"
                                className="mb-4"
                            />

                            <div className="text-center text-sm text-gray-500">
                                รูปภาพทั้งหมด {files.length} รูป - คลิกที่รูปเพื่อดู
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

export default ImageUploadModal;